import type { ITransformRT2 } from '@seddi/math-v2';
import type { BufferGeometry, Vector3 } from '@seddi/three';
import type { VertexDataDesc } from '../core/bufferTypes';
import { computeTransformBasis as computeTransformBasisFromVd } from '../geometry/triMeshTransformBasis';
import { SceneGeometryModule } from '../scene/sceneGeometryModule';

type Basis = { normal: Vector3; position: Vector3; tangent: Vector3 };

type GeometryRenderable = { geometry: BufferGeometry; id: string };

/**
 * Compute piece 3D basis from 2D transform.
 * @param mesh - Piece mesh.
 * @param transform - Transform.
 * @param ref - Target basis.
 */
export const computeTransformBasis = (
  self: Readonly<GeometryRenderable>,
  transform: ITransformRT2,
  ref: Basis,
) => {
  const data = extractGeometryData(self.geometry);
  // Update 2D query acceleration structures
  const geometryCache = SceneGeometryModule.instance.geometryCache;
  const geometryCachePiece = geometryCache.touchPiece(self.id);
  // TODO [Ivan]: Get cell size from scene resolution.
  const cellSize = 0.015;
  geometryCachePiece.updateGrid(data.indices as Uint32Array, data.uvs, cellSize);
  // Compute basis.
  computeTransformBasisFromVd(data, geometryCachePiece.grid, transform, ref);
};

/**
 * Adapt geometry.
 * @param geometry - Buffer geometry.
 */
const extractGeometryData = (geometry: BufferGeometry): VertexDataDesc => {
  const data: VertexDataDesc = {
    indices: geometry.getIndex().array as Uint32Array,
    positions: geometry.getAttribute('position').array as Float32Array,
    normals: geometry.getAttribute('normal').array as Float32Array,
    uvs: geometry.getAttribute('uvs').array as Float32Array,
  };
  return data;
};
